import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './styles.css';

const API_BASE = 'https://starfolio-worker.maggiecoronel8.workers.dev';

// Types
interface User {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
}

interface Stats {
  total_tenants: number;
  active_tenants: number;
  suspended_tenants: number;
  total_subscribers: number;
  total_orders: number;
  total_revenue: number;
  total_debt: number;
}

interface Tenant {
  id: string;
  bot_username: string;
  status: 'active' | 'suspended' | 'banned';
  debt_stars: number;
  commission_rate: number;
  created_at: number;
}

interface Order {
  id: string;
  title: string;
  bot_username: string;
  amount_stars: number;
  paid_at: number;
}

declare global {
  interface Window {
    Telegram: {
      WebApp: {
        initData: string;
        initDataUnsafe: { user?: User };
        themeParams: any;
        colorScheme: 'light' | 'dark';
        expand(): void;
        ready(): void;
        HapticFeedback: {
          impactOccurred(style: string): void;
          notificationOccurred(type: string): void;
        };
        MainButton: any;
        showPopup(params: any): void;
        showConfirm(message: string, callback: (ok: boolean) => void): void;
      };
    };
  }
}

function App() {
  const tg = window.Telegram?.WebApp;
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'tenants' | 'add'>('dashboard');
  
  const [stats, setStats] = useState<Stats | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  
  const [newBotToken, setNewBotToken] = useState('');
  const [newCommissionRate, setNewCommissionRate] = useState(0.10);
  const [clientTgId, setClientTgId] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (tg) {
      tg.ready();
      tg.expand();
      
      const user = tg.initDataUnsafe?.user;
      if (user) {
        setAuthenticated(true);
        loadDashboard();
      } else {
        setError('Please open this app from Telegram');
        setLoading(false);
      }
    } else {
      setError('Please open this app from Telegram');
      setLoading(false);
    }
  }, []);

  const loadDashboard = async () => {
    try {
      console.log('Loading dashboard, initData present:', !!tg.initData);
      console.log('API Base:', API_BASE);
      
      const res = await fetch(`${API_BASE}/api/admin/dashboard`, {
        headers: { 'Authorization': `tma ${tg.initData}` }
      });
      
      console.log('Response status:', res.status);

      if (res.ok) {
        const data = await res.json();
        setStats(data.stats);
        setTenants(data.recent_tenants);
        setRecentOrders(data.recent_orders);
      } else {
        const error = await res.json();
        console.error('Dashboard error:', error);
        setError(error.error || 'Failed to load dashboard');
      }
      setLoading(false);
    } catch (e) {
      console.error('Failed to load dashboard:', e);
      setError('Failed to connect to server. Check console for details.');
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/tenants`, {
        headers: { 'Authorization': `tma ${tg.initData}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setTenants(data);
      }
    } catch (e) {
      console.error('Failed to load tenants:', e);
    }
  };

  const handleAddTenant = async () => {
    if (!newBotToken || !clientTgId) {
      setError('Please fill in all fields');
      return;
    }

    if (!tg.initData) {
      setError('Telegram authentication required. Please restart the app from Telegram.');
      return;
    }

    setProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch(`${API_BASE}/api/admin/tenants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `tma ${tg.initData}`
        },
        body: JSON.stringify({
          botToken: newBotToken,
          clientTgId: parseInt(clientTgId),
          commissionRate: newCommissionRate
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to add tenant');
      }

      setSuccess(`Tenant @${data.botUsername} added successfully!`);
      setNewBotToken('');
      setClientTgId('');
      setActiveTab('dashboard');
      loadDashboard();
      
      tg?.HapticFeedback?.notificationOccurred('success');
    } catch (err: any) {
      console.error('Add tenant error:', err);
      setError(err.message || 'Failed to add tenant. Check console for details.');
      tg?.HapticFeedback?.notificationOccurred('error');
    } finally {
      setProcessing(false);
    }
  };

  const handleSuspendTenant = async (tenantId: string, botUsername: string) => {
    tg?.showConfirm(`Suspend @${botUsername}?`, async (ok) => {
      if (!ok) return;

      try {
        const res = await fetch(`${API_BASE}/api/admin/tenants/${tenantId}/suspend`, {
          method: 'POST',
          headers: { 'Authorization': `tma ${tg.initData}` }
        });

        if (res.ok) {
          setSuccess(`@${botUsername} suspended`);
          loadDashboard();
          tg?.HapticFeedback?.notificationOccurred('success');
        }
      } catch (e) {
        setError('Failed to suspend tenant');
        tg?.HapticFeedback?.notificationOccurred('error');
      }
    });
  };

  const handleActivateTenant = async (tenantId: string, botUsername: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/tenants/${tenantId}/activate`, {
        method: 'POST',
        headers: { 'Authorization': `tma ${tg.initData}` }
      });

      if (res.ok) {
        setSuccess(`@${botUsername} activated`);
        loadDashboard();
        tg?.HapticFeedback?.notificationOccurred('success');
      }
    } catch (e) {
      setError('Failed to activate tenant');
      tg?.HapticFeedback?.notificationOccurred('error');
    }
  };

  const handleDeleteTenant = async (tenantId: string, botUsername: string) => {
    tg?.showConfirm(`DELETE @${botUsername}? This cannot be undone!`, async (ok) => {
      if (!ok) return;

      try {
        const res = await fetch(`${API_BASE}/api/admin/tenants/${tenantId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `tma ${tg.initData}` }
        });

        if (res.ok) {
          setSuccess(`@${botUsername} deleted`);
          loadDashboard();
          tg?.HapticFeedback?.notificationOccurred('success');
        }
      } catch (e) {
        setError('Failed to delete tenant');
        tg?.HapticFeedback?.notificationOccurred('error');
      }
    });
  };

  const theme = tg?.themeParams || {};
  const isDark = tg?.colorScheme === 'dark';

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-gradient"></div>
        <div className="loading-content">
          <div className="loading-logo">
            <div className="logo-circle">
              <div className="logo-star">⭐</div>
            </div>
          </div>
          <p className="loading-text">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="error-screen">
        <div className="error-card">
          <div className="error-icon">🔒</div>
          <h2>Access Required</h2>
          <p>{error || 'Please open from Telegram'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`app ${isDark ? 'dark' : 'light'}`}>
      {/* Animated Background */}
      <div className="app-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="header-left">
            <div className="logo">
              <div className="logo-icon">
                <span className="star">⭐</span>
              </div>
              <div>
                <h1>StarFolio</h1>
                <p className="subtitle">Admin Dashboard</p>
              </div>
            </div>
          </div>
          <div className="header-right">
            {tg?.initDataUnsafe?.user?.photo_url && (
              <img 
                src={tg.initDataUnsafe.user.photo_url} 
                alt="Profile" 
                className="user-avatar" 
              />
            )}
            <div className="user-name">
              {tg?.initDataUnsafe?.user?.first_name || 'Admin'}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="nav-tabs">
          <button 
            className={`nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => { setActiveTab('dashboard'); loadDashboard(); }}
          >
            <span className="tab-icon">📊</span>
            <span>Overview</span>
          </button>
          <button 
            className={`nav-tab ${activeTab === 'tenants' ? 'active' : ''}`}
            onClick={() => { setActiveTab('tenants'); loadTenants(); }}
          >
            <span className="tab-icon">🏢</span>
            <span>Tenants</span>
          </button>
          <button 
            className={`nav-tab ${activeTab === 'add' ? 'active' : ''}`}
            onClick={() => setActiveTab('add')}
          >
            <span className="tab-icon">➕</span>
            <span>Add New</span>
          </button>
        </nav>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {error && (
          <div className="notification error">
            <div className="notification-content">
              <span className="notification-icon">⚠️</span>
              <span>{error}</span>
            </div>
            <button onClick={() => setError(null)} className="notification-close">✕</button>
          </div>
        )}
        
        {success && (
          <div className="notification success">
            <div className="notification-content">
              <span className="notification-icon">✅</span>
              <span>{success}</span>
            </div>
            <button onClick={() => setSuccess(null)} className="notification-close">✕</button>
          </div>
        )}

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div className="dashboard-content">
            {/* Welcome Card */}
            <div className="welcome-card">
              <div className="welcome-content">
                <h2>Welcome back, {tg?.initDataUnsafe?.user?.first_name || 'Admin'}! 👋</h2>
                <p>Here's what's happening with your platform today.</p>
              </div>
              <div className="welcome-decoration">🚀</div>
            </div>

            {/* Stats Grid */}
            <div className="stats-grid">
              <StatCard 
                title="Total Tenants" 
                value={stats.total_tenants} 
                icon="🏢" 
                gradient="blue"
                trend="+12%"
              />
              <StatCard 
                title="Active" 
                value={stats.active_tenants} 
                icon="✅" 
                gradient="green"
                trend="98%"
              />
              <StatCard 
                title="Subscribers" 
                value={stats.total_subscribers} 
                icon="👥" 
                gradient="purple"
                trend="+24%"
              />
              <StatCard 
                title="Total Revenue" 
                value={`${stats.total_revenue} ⭐`} 
                icon="💰" 
                gradient="gold"
                trend="+18%"
              />
            </div>

            {/* Two Column Layout */}
            <div className="dashboard-grid">
              {/* Recent Tenants */}
              <div className="card modern-card">
                <div className="card-header">
                  <div className="card-title">
                    <span className="card-icon">🏢</span>
                    <h3>Recent Tenants</h3>
                  </div>
                  <button className="card-action" onClick={() => setActiveTab('tenants')}>
                    View All
                    <span className="arrow">→</span>
                  </button>
                </div>
                <div className="card-body">
                  <div className="list">
                    {tenants.slice(0, 5).map((tenant, index) => (
                      <div key={tenant.id} className="list-item">
                        <div className="list-item-left">
                          <div className="list-item-icon">
                            {tenant.status === 'active' ? '🟢' : tenant.status === 'suspended' ? '🟡' : '🔴'}
                          </div>
                          <div>
                            <div className="list-item-title">@{tenant.bot_username}</div>
                            <div className="list-item-subtitle">
                              {tenant.debt_stars} ⭐ debt
                            </div>
                          </div>
                        </div>
                        <div className="list-item-badge">
                          {tenant.status}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Recent Orders */}
              <div className="card modern-card">
                <div className="card-header">
                  <div className="card-title">
                    <span className="card-icon">💰</span>
                    <h3>Recent Orders</h3>
                  </div>
                </div>
                <div className="card-body">
                  <div className="list">
                    {recentOrders.slice(0, 5).map((order) => (
                      <div key={order.id} className="list-item">
                        <div className="list-item-left">
                          <div className="list-item-icon">⭐</div>
                          <div>
                            <div className="list-item-title">{order.title}</div>
                            <div className="list-item-subtitle">
                              @{order.bot_username}
                            </div>
                          </div>
                        </div>
                        <div className="list-item-amount">
                          +{order.amount_stars} ⭐
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tenants Tab */}
        {activeTab === 'tenants' && (
          <div className="tenants-content">
            <div className="page-header">
              <h2>🏢 All Tenants</h2>
              <div className="page-subtitle">
                {tenants.length} {tenants.length === 1 ? 'tenant' : 'tenants'} managed
              </div>
            </div>

            <div className="tenants-grid">
              {tenants.map(tenant => (
                <div key={tenant.id} className="tenant-card-modern">
                  <div className="tenant-card-header">
                    <div className="tenant-avatar">
                      {tenant.bot_username.charAt(1).toUpperCase()}
                    </div>
                    <div className="tenant-info">
                      <h3>@{tenant.bot_username}</h3>
                      <div className="tenant-meta">
                        <span className={`status-badge ${tenant.status}`}>
                          {tenant.status}
                        </span>
                        <span className="commission">
                          {(tenant.commission_rate * 100).toFixed(0)}% fee
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="tenant-stats">
                    <div className="tenant-stat">
                      <span className="stat-label">Debt</span>
                      <span className={`stat-value ${tenant.debt_stars > 0 ? 'warning' : ''}`}>
                        {tenant.debt_stars} ⭐
                      </span>
                    </div>
                    <div className="tenant-stat">
                      <span className="stat-label">Created</span>
                      <span className="stat-value-small">
                        {new Date(tenant.created_at * 1000).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="tenant-actions">
                    {tenant.status === 'active' ? (
                      <button 
                        className="btn btn-warning"
                        onClick={() => handleSuspendTenant(tenant.id, tenant.bot_username)}
                      >
                        ⏸️ Suspend
                      </button>
                    ) : (
                      <button 
                        className="btn btn-success"
                        onClick={() => handleActivateTenant(tenant.id, tenant.bot_username)}
                      >
                        ▶️ Activate
                      </button>
                    )}
                    <button 
                      className="btn btn-danger"
                      onClick={() => handleDeleteTenant(tenant.id, tenant.bot_username)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add Tenant Tab */}
        {activeTab === 'add' && (
          <div className="add-content">
            <div className="page-header">
              <h2>➕ Add New Tenant</h2>
              <div className="page-subtitle">
                Onboard a new business to the platform
              </div>
            </div>

            <div className="card modern-card form-card">
              <div className="card-body">
                <div className="form-group">
                  <label className="form-label">
                    <span className="label-icon">🤖</span>
                    Bot Token
                  </label>
                  <input
                    type="text"
                    value={newBotToken}
                    onChange={(e) => setNewBotToken(e.target.value)}
                    placeholder="123456:ABCdefGHIjkl..."
                    className="form-input"
                  />
                  <div className="form-hint">
                    Get this from @BotFather in Telegram
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <span className="label-icon">👤</span>
                    Client Telegram ID
                  </label>
                  <input
                    type="number"
                    value={clientTgId}
                    onChange={(e) => setClientTgId(e.target.value)}
                    placeholder="6858953797"
                    className="form-input"
                  />
                  <div className="form-hint">
                    User can get this from @userinfobot
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <span className="label-icon">💎</span>
                    Commission Rate: {(newCommissionRate * 100).toFixed(0)}%
                  </label>
                  <div className="slider-container">
                    <input
                      type="range"
                      min="0"
                      max="50"
                      value={newCommissionRate * 100}
                      onChange={(e) => setNewCommissionRate(parseInt(e.target.value) / 100)}
                      className="form-slider"
                    />
                    <div className="slider-labels">
                      <span>0%</span>
                      <span>25%</span>
                      <span>50%</span>
                    </div>
                  </div>
                  <div className="form-hint">
                    Platform fee from each transaction
                  </div>
                </div>

                <button 
                  className="btn btn-primary btn-large"
                  onClick={handleAddTenant}
                  disabled={processing || !newBotToken || !clientTgId}
                >
                  {processing ? (
                    <>
                      <div className="btn-spinner"></div>
                      Creating Tenant...
                    </>
                  ) : (
                    <>
                      <span className="btn-icon">✨</span>
                      Create Tenant
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ title, value, icon, gradient, trend }: { 
  title: string; 
  value: string | number; 
  icon: string;
  gradient: string;
  trend: string;
}) {
  return (
    <div className={`stat-card gradient-${gradient}`}>
      <div className="stat-card-content">
        <div className="stat-card-top">
          <span className="stat-card-icon">{icon}</span>
          <span className="stat-card-trend">{trend}</span>
        </div>
        <div className="stat-card-value">{value}</div>
        <div className="stat-card-title">{title}</div>
      </div>
      <div className="stat-card-decoration"></div>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(<App />);
